import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import FlexBox from 'flexbox-react';
import Helmet from 'react-helmet';
import { ThemeProvider } from 'react-css-themr';
import { findIndex, find } from 'lodash';
import cx from 'classnames';
import { can } from '../../../../../services';
import styles from 'phaseDetailsBar.scss';
import { connect } from 'react-redux';
import connectUI from 'redux-ui';
import { Tabs, Tab } from 'react-toolbox';
import { filterApptsByTodayOrLater } from '../../../../../utils/dateUtils';
import { validateSource } from '../../../../../utils/index.js';
import { loadPhase, navigate, requestEmployee } from 'actionCreators';
import contentAreaStyles from 'styles/contentArea';
import PhaseTaskBar from 'phaseTaskBar';
import PhaseActionMenu from 'phaseActionMenu';
import PhaseDetailsBar from '../components/phaseDetailsBar.jsx';
import Loading from 'components/Loading';
import MessageBox from 'components/MessageBox';
import { PHASE_DETAILS_TABS, SCRIPT_STATUSES, VENDORS } from 'config/constants';
import PhaseType from 'types/phase';
import PhaseDetails from './tabs/phaseDetails';
import PhaseDocuments from './tabs/phaseDocuments';
import PhaseHistory from './tabs/phaseHistory';
import PhaseAccounting from './tabs/phaseAccounting';
import PhaseContractVerificationChecklist from './tabs/phaseContractVerificationChecklist';
import PhaseSchedulingChecklist from './tabs/phaseSchedulingChecklist';
import PhaseAppointments from './tabs/phaseAppointments';
import PhaseContract from './tabs/phaseContractVerification';
import RelatedPhases from './tabs/relatedPhases';
import WarehouseMaterials from './tabs/warehouseMaterials';
// import PhaseSurvey from './tabs/phaseSurvey';
import PhaseCommunications from './tabs/phaseCommunications';
import PhaseMaterialsChecklist from './tabs/phaseMaterialsChecklist';

const QUICK_SCHEDULE_POLL_MS = 60000;

class PhaseShow extends React.Component {
  componentDidMount = () => {
    const { id, phase, employeeId, employeeType,
            loadPhase: propLoad, requestEmployee: propEmployee } = this.props;
    this.updateActiveTabFromLocation();
    if (!phase || id !== phase.id) propLoad(id);
    if (employeeType === 'Employee') propEmployee(employeeId);
    if (this.shouldQuickSchedulePoll(phase, this.props.location)) this.startQuickSchedulePoll();
  }

  componentWillReceiveProps = (nextProps) => {
    const { id, location: { pathname }, loadPhase: propLoad } = this.props;
    if (id !== nextProps.id) propLoad(nextProps.id).then(this.updateActiveTabFromLocation);
    else if (pathname !== nextProps.location.pathname) this.updateActiveTabFromLocation(nextProps);

    const wasPolling = !!this.quickSchedulePoll;
    const willPoll = this.shouldQuickSchedulePoll(nextProps.phase, nextProps.location);
    if (willPoll && !wasPolling) this.startQuickSchedulePoll();
    else if (!willPoll && wasPolling) this.stopQuickSchedulePoll();
  }

  componentWillUnmount = () => {
    this.stopQuickSchedulePoll();
    return this.props.ui.successMessage ? this.props.updateUI('successMessage', '') : null;
  }

  getPhaseTabs = () => {
    const { phase: { phase_tasks, status, type, appointments = [] } } = this.props;
    return PHASE_DETAILS_TABS
      .filter(tab => tab.phaseTypes.indexOf(type) > -1)
      .filter(tab => this.taskAndStatusFilters(tab.label, status, phase_tasks, appointments));
  }

  getTabBarColor = (phaseObj) => {
    const defaultStyle = styles.tabs;
    if (phaseObj) {
      const { source = null, workroom = null } = phaseObj;
      let vendor = source;
      if (workroom && workroom.name.includes('PFT-')) vendor = 'PFT';
      else if (!VENDORS.includes(this.isLhi() ? 'LHI' : vendor)) return defaultStyle;
      return styles[`${vendor.toLowerCase()}Tabs`];
    }
    return defaultStyle;
  }

  getRelatedColor = () => (this.isLhi() ? styles.lowesRelatedPhases : styles.relatedCount);

  shouldQuickSchedulePoll = (phase, location) => (
    !!(phase && phase.pending_quick_schedule_response &&
       location && location.pathname &&
       location.pathname.indexOf('/appointments') > -1)
  )

  startQuickSchedulePoll = () => {
    if (this.quickSchedulePoll) return;
    this.quickSchedulePoll = setInterval(
      () => this.props.loadPhase(this.props.id),
      QUICK_SCHEDULE_POLL_MS,
    );
  }

  stopQuickSchedulePoll = () => {
    if (!this.quickSchedulePoll) return;
    clearInterval(this.quickSchedulePoll);
    this.quickSchedulePoll = null;
  }

  isLhi = () => validateSource(this.props.phase.source);

  taskAndStatusFilters = (tab, status, phaseTasks, phaseAppts) => {
    switch (tab) {
      case 'Contract Verification Checklist':
        return this.cVFilter(status, phaseTasks);
      case 'Scheduling Checklist':
        return this.schedulingFilter(status, phaseTasks);
      case 'Communication Scripts':
        return this.scriptsFilter(status, phaseTasks, phaseAppts);
      default:
        return true;
    }
  }

  isTaskStatusComplete = (tasks, name) => (find(tasks, { name }) || {}).status === 'complete';

  basicStatusFilter = (tab, requiredStatus, status, phaseTasks) =>
    !(status !== requiredStatus || this.isTaskStatusComplete(phaseTasks, tab));


  cVFilter = (tab, status, phaseTasks) => this.basicStatusFilter(tab, 'receiving', status, phaseTasks);

  schedulingFilter = (tab, status, phaseTasks) => this.basicStatusFilter(tab, 'scheduling', status, phaseTasks);

  scriptsFilter = (status, phaseTasks, phaseAppts) => {
    if (!SCRIPT_STATUSES.includes(status)) return false;
    const { phase: { type = '' } } = this.props;
    switch (status) {
      case SCRIPT_STATUSES[0]:
        return this.validApptsExist(phaseAppts) && !this.isTaskStatusComplete(phaseTasks, 'Customer Review');
      case SCRIPT_STATUSES[1]:
      case SCRIPT_STATUSES[2]:
        return !this.isTaskStatusComplete(phaseTasks, 'In Progress Call');
      case SCRIPT_STATUSES[3]:
        return type === 'Installation' && !this.isTaskStatusComplete(phaseTasks, 'Follow Up Call');
      case SCRIPT_STATUSES[4]:
        return type === 'Installation' && !this.isTaskStatusComplete(phaseTasks, 'Follow Up Delivery Call');
      default:
        return false;
    }
  }

  validApptsExist = phaseAppts =>
    [phaseAppts.length, filterApptsByTodayOrLater(phaseAppts).length].every(l => l > 0);

  updateActiveTabFromLocation = (props = this.props) => {
    const { location, updateUI } = props;
    const tabObject = find(PHASE_DETAILS_TABS, tab => location.pathname.indexOf(`/${tab.path}`) > -1);
    if (!tabObject) return;
    updateUI('activeTab', tabObject.path);
  }

  handleTabChange = (tabIndex) => {
    const path = this.getPhaseTabs()[tabIndex].path;
    this.props.updateUI('activeTab', path);
    this.props.navigate(`/phases/${this.props.id}/${path}`);
  }

  render = () => {
    const { loading, phase, ui, role, position, relatedCount,
            employeeType, canViewWorkroom, mvw } = this.props;
    return (loading ? (<Loading />) : (<Fragment>
      <Helmet title={phase.customer_name} />
      <PhaseDetailsBar
        phase={phase}
        role={role}
        position={position}
        mvw={mvw}
        canViewWorkroom={canViewWorkroom}
      />
      <PhaseTaskBar phase={phase} role={role} employeeType={employeeType} />
      <ThemeProvider theme={{ RTTabs: styles }}>
        <Tabs
          index={findIndex(this.getPhaseTabs(), tab => tab.path === ui.activeTab)}
          onChange={this.handleTabChange}
          className={this.getTabBarColor(phase)}
        >
          {this.getPhaseTabs().map((tab) => {
            const { label, ability } = tab;
            const relatedTab = label === 'Related Phases';
            return (<Tab
              key={tab.routeName}
              label={relatedTab ? `${label} (${relatedCount})` : label}
              className={cx({
                [styles.phaseTab]: true,
                [this.getRelatedColor()]: relatedTab && relatedCount > 0,
                [styles.hidden]: !can(role, 'view', ability) || (phase.source === 'IFI' && label === 'Materials'),
              })}
            />);
          })}
        </Tabs>
      </ThemeProvider>
      <FlexBox>
        <div
          className={contentAreaStyles.contentAreaContainer}
          style={{ paddingBottom: '100px' }}
        >
          {ui.successMessage ? <MessageBox message={ui.successMessage} /> : null}
          {this.props.children}
        </div>
      </FlexBox>
      <PhaseActionMenu phase={phase} role={role} position={position} mvw={mvw} />
    </Fragment>));
  }
}

PhaseShow.propTypes = {
  id: PropTypes.number,
  employeeId: PropTypes.number,
  employeeType: PropTypes.string,
  ui: PropTypes.shape({
    activeTab: PropTypes.string,
    successMessage: PropTypes.string,
  }),
  loading: PropTypes.bool,
  phase: PhaseType,
  role: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  mvw: PropTypes.object,
  updateUI: PropTypes.func.isRequired,
  loadPhase: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  requestEmployee: PropTypes.func.isRequired,
  children: PropTypes.element.isRequired,
  location: PropTypes.object,
  relatedCount: PropTypes.number,
  canViewWorkroom: PropTypes.bool,
};

const mapStateToProps = (state, ownProps) => {
  const { user: { employable_id, employable_type, role, position,
                  manage_vendor_workrooms: mvw = {} },
          phase: { details, details: { loaded }, related }, phase } = state;
  return {
    id: Number(ownProps.params.id),
    phase: loaded ? details : null,
    relatedCount: related.count,
    role,
    position,
    mvw,
    employeeId: employable_id,
    employeeType: employable_type,
    loading: phase ? !loaded : true,
    canViewWorkroom: can(role, 'view', 'workroom'),
  };
};

export default connectUI()(connect(mapStateToProps, {
  loadPhase, navigate, requestEmployee,
})(PhaseShow));

export {
  PhaseHistory,
  PhaseContract,
  PhaseDetails,
  PhaseDocuments,
  PhaseAccounting,
  PhaseContractVerificationChecklist,
  PhaseSchedulingChecklist,
  PhaseAppointments,
  RelatedPhases,
  WarehouseMaterials,
  // PhaseSurvey,
  PhaseCommunications,
  PhaseMaterialsChecklist,
};



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/index.jsx